import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { AbilityServiceSignal } from '@casl/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import * as Sentry from '@sentry/angular';
import { UserVacationV2 } from '@wilson/absences';
import { FetchAllUsersIfNeeded, UsersState } from '@wilson/account';
import { AccountValueGetterService } from '@wilson/account/ag-grid';
import {
  defaultColDef,
  defaultGridOptionsFactory,
} from '@wilson/ag-grid/configuration';
import { GridService } from '@wilson/ag-grid/service';
import { AuthorizationModule } from '@wilson/authorization';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import { FilterModule } from '@wilson/filter';
import {
  OrganizationalUnit,
  RoleAction,
  RolePermissionSubject,
  UserVacation,
  UserWithVacationStatus,
} from '@wilson/interfaces';
import {
  FetchOrgUnits,
  OrganizationalUnitsState,
  OrgUnitNamePipeModule,
} from '@wilson/organizational-unit';
import { PageFrameModule } from '@wilson/page-frame';
import {
  EmptyStateModule,
  LoadingSpinnerModule,
} from '@wilson/shared-components';
import { AgGridModule } from 'ag-grid-angular';
import { ColDef, ValueGetterParams } from 'ag-grid-community';
import { getYear } from 'date-fns';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { filter, finalize, map, Observable, switchMap, take, tap } from 'rxjs';
import { VacationManagementV2Service } from '../../vacation-management-v2.service';
import { VacationManagementService } from '../../vacation-management.service';
import { EmployeeOrgUnitNameRendererComponent } from './renderers/employee-org-unit-name.renderer';
import { VacationManagementDrawerComponent } from './vacation-management-drawer/vacation-management-drawer.component';
import { LocalizedDateFormatPipe } from '@wilson/localization';
import { ProgressMessageComponent } from '@wilson/non-domain-specific/shift-helpers/core';
import { LocalizedNumberPipe } from '@wilson/pipes';
import { VacationManagementV2BatchingService } from './vacation-management-v2-batching.service';

@Component({
  selector: 'wilson-vacation-management-v2',
  imports: [
    CommonModule,
    AgGridModule,
    PageFrameModule,
    NzButtonModule,
    FeatureFlagsModule,
    TranslateModule,
    FeatureFlagsModule,
    NzSelectModule,
    FilterModule,
    EmptyStateModule,
    FormsModule,
    NzDatePickerModule,
    VacationManagementDrawerComponent,
    LoadingSpinnerModule,
    AuthorizationModule,
    NzTooltipModule,
    OrgUnitNamePipeModule,
    ProgressMessageComponent,
  ],
  templateUrl: './vacation-managementv2.component.html',
  styleUrl: './vacation-managementv2.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    GridService,
    VacationManagementV2BatchingService,
    LocalizedNumberPipe,
  ],
})
export class VacationManagementv2Component implements OnInit {
  readonly gridService =
    inject<GridService<UserWithVacationStatus>>(GridService);
  readonly vacationManagementV2Service = inject(VacationManagementV2Service);
  private store = inject(Store);
  readonly accountValueGetterService = inject(AccountValueGetterService);
  readonly vacationManagementService = inject(VacationManagementService);
  private readonly nzMessageService = inject(NzMessageService);
  private readonly translateService = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private readonly localizationDatePipe = inject(LocalizedDateFormatPipe);
  private readonly localizedNumberPipe = inject(LocalizedNumberPipe);
  private readonly batchService = inject(VacationManagementV2BatchingService);

  readonly progressMessage =
    viewChild.required<ProgressMessageComponent>('progressMessage');

  RoleAction = RoleAction;
  RolePermissionSubject = RolePermissionSubject;

  selectedYear: Date | null = new Date();
  userVacations$!: Observable<UserVacationV2[]>;

  readonly isLoadingSignal = signal(false);
  readonly isExporting = signal(false);

  isLoadingVacationData = false;
  selectedUserVacations: UserVacationV2[] = [];
  visible = false;
  showMask = false;
  can = inject(AbilityServiceSignal).can;
  public readonly defaultColDef: ColDef = defaultColDef;
  public readonly gridOptions = defaultGridOptionsFactory();

  private currentYear: number = getYear(new Date());
  private orgUnitsMap!: Map<string | undefined, OrganizationalUnit>;

  readonly currentUserPageSignal = signal(0);
  components = {
    employeeOrgUnitNameRendererComponent: EmployeeOrgUnitNameRendererComponent,
  };

  protected columnDefs: ColDef[] = [
    {
      headerName: this.translateService.instant('general.name'),
      resizable: true,
      flex: 1,
      field: 'name',
      sort: 'asc',
      valueGetter: this.accountValueGetterService.userNameValueGetter,
      checkboxSelection: true,
      headerCheckboxSelectionFilteredOnly: false,
      headerCheckboxSelection: true,
    },
    {
      headerName: this.translateService.instant(
        'page.vacation.management.organizational_unit',
      ),
      sortable: true,
      flex: 1,
      cellRenderer: 'employeeOrgUnitNameRendererComponent',
      field: 'orgUnitId',
      comparator: (valueA: string, valueB: string): number => {
        const resolvedValueA = this.orgUnitsMap.get(valueA)?.name || '';
        const resolvedValueB = this.orgUnitsMap.get(valueB)?.name || '';
        return resolvedValueA.localeCompare(resolvedValueB);
      },
    },
    {
      headerName: this.translateService.instant(
        'page.vacation_management.leave_days',
      ),
      sortable: true,
      flex: 1,
      field: 'leaveDays',
      valueGetter: this.numberOfLeaveDaysValueGetter.bind(this),
    },
    {
      headerName: this.translateService.instant(
        'page.vacation.management.special_leave_days',
      ),
      flex: 1,
      field: 'specialLeaveDays',
      valueGetter: this.numberOfSpecialLeaveDaysValueGetter.bind(this),
    },
    {
      headerName: this.translateService.instant(
        'page.vacation.management.carried_over',
      ),
      flex: 1,
      field: 'carriedOver',
      valueGetter: this.numberOfCarriedOverDaysValueGetter.bind(this),
    },
    {
      headerName: this.translateService.instant(
        'page.vacation.management.used_leave_days',
      ),
      flex: 1,
      field: 'usedLeaveDays',
      valueGetter: this.numberOfUsedLeaveDaysValueGetter.bind(this),
    },
    {
      headerName: this.translateService.instant(
        'page.vacation.management.used_special_leave_days',
      ),
      flex: 1,
      field: 'usedSpecialLeaveDays',
      valueGetter: this.numberOfUsedSpecialLeaveDaysValueGetter.bind(this),
    },
    {
      headerName: this.translateService.instant(
        'page.vacation.management.planned_leave_days',
      ),
      flex: 1,
      field: 'plannedLeaveDays',
      valueGetter: this.numberOfPlannedLeaveDaysValueGetter.bind(this),
    },
    {
      headerName: this.translateService.instant(
        'page.vacation.management.planned_special_leave_days',
      ),
      flex: 1,
      field: 'plannedSpecialLeaveDays',
      valueGetter: this.numberOfPlannedSpecialLeaveDaysValueGetter.bind(this),
    },
    {
      headerName: this.translateService.instant(
        'page.vacation.management.balance',
      ),
      flex: 1,
      field: 'balance',
      valueGetter: this.balanceDaysValueGetter.bind(this),
    },
  ];

  ngOnInit(): void {
    this.store.dispatch(new FetchAllUsersIfNeeded());
    this.initializeOrgUnits();
    const year = getYear(new Date());
    this.loadUserVacationsByYear(year);
  }

  private initializeOrgUnits(): void {
    this.store
      .dispatch(new FetchOrgUnits())
      .pipe(
        switchMap(() => this.store.select(OrganizationalUnitsState.orgUnits)),
        takeUntilDestroyed(this.destroyRef),
        tap((units) => {
          this.orgUnitsMap = new Map(units.map((u) => [u.id, u]));
        }),
      )
      .subscribe();
  }

  onSelectionChanged(): void {
    this.selectedUserVacations =
      this.gridService.gridApi?.getSelectedRows() || [];
  }

  private resolveVacationsForSave(
    vacations: UserVacationV2[],
    year: number,
  ): UserVacationV2[] {
    return vacations.map((v) => {
      // check if vacation exist in current table per year
      if (v.id != null) {
        return v;
      }
      const cached = this.batchService.getCachedVacation(v.userId, year);

      // check if cached vacation exists
      if (!cached) {
        return v;
      }
      return {
        ...v,
        id: cached.id,
        availableVacationDays:
          v.availableVacationDays ?? cached.availableVacationDays,
        availableSpecialVacationDays:
          v.availableSpecialVacationDays ?? cached.availableSpecialVacationDays,
        carryover: v.carryover ?? cached.carryover,
      };
    });
  }

  private async hydrateVacations(
    vacations: UserVacationV2[],
    year: number,
  ): Promise<UserVacationV2[]> {
    const batches = this.batchService.createBatches(vacations, year);
    if (batches.length) {
      await this.batchService.processInBatches(
        batches,
        this.batchService.MAX_PARALLEL_BATCHES,
        (batch) => this.batchService.fetchAndCacheBatch(batch, year),
      );
    }
    return this.resolveVacationsForSave(vacations, year);
  }

  private async cacheVacationsForExport(
    vacations: UserVacationV2[],
    year: number,
  ): Promise<void> {
    const batches = this.batchService.createBatches(vacations, year);
    if (batches.length) {
      let completedBatches = 0;
      await this.batchService.processInBatches(
        batches,
        this.batchService.MAX_PARALLEL_BATCHES,
        (batch) => this.batchService.fetchAndCacheBatch(batch, year),
        () => {
          completedBatches++;
          this.progressMessage().setProgress(
            (completedBatches / batches.length) * 100,
          );
        },
      );
    } else {
      this.progressMessage().setProgress(100);
    }
  }

  private getVisibleUserIds(): string[] {
    if (!this.gridService.gridApi) {
      return [];
    }

    const rowsToDisplay = this.gridService.gridApi.paginationGetPageSize();
    const currentPage = this.gridService.gridApi.paginationGetCurrentPage();
    const startRow = currentPage * rowsToDisplay;
    const userIds: string[] = [];

    for (let i = startRow; i < startRow + rowsToDisplay; i++) {
      const rowNode = this.gridService.gridApi.getDisplayedRowAtIndex(i);
      if (rowNode?.data?.userId) {
        userIds.push(rowNode.data.userId);
      }
    }

    return userIds;
  }

  private loadVacationsForVisibleRows(): void {
    const visibleUserIds = this.getVisibleUserIds();
    if (visibleUserIds.length === 0) {
      return;
    }

    const uncachedUserIds = visibleUserIds.filter(
      (userId) =>
        !this.batchService.getCachedVacation(userId, this.currentYear),
    );
    if (uncachedUserIds.length === 0) {
      return;
    }

    this.isLoadingVacationData = true;
    this.cdr.detectChanges();

    this.vacationManagementV2Service
      .getUsersVacationStatus({
        where: { year: this.currentYear, userIds: uncachedUserIds },
      })
      .pipe(take(1))
      .subscribe((vacations) => {
        this.batchService.setCachedVacations(vacations, this.currentYear);

        if (this.gridService.gridApi) {
          this.gridService.gridApi.forEachNode((node) => {
            const vacation = this.batchService.getCachedVacation(
              node.data.userId,
              this.currentYear,
            );
            if (vacation) {
              node.setData({ ...node.data, ...vacation });
            }
          });
        }

        this.isLoadingVacationData = false;
        this.cdr.detectChanges();
      });
  }

  onPaginationChanged(): void {
    if (!this.isLoadingVacationData) {
      this.loadVacationsForVisibleRows();
    }
  }

  loadUserVacationsByYear(year: number, navigateToSpecificPage = false): void {
    this.isLoadingSignal.set(true);
    this.currentYear = year;
    this.batchService.clearCache();

    if (this.gridService.gridApi) {
      this.gridService.gridApi.paginationGoToFirstPage();
    }

    this.userVacations$ = this.store.select(UsersState.users).pipe(
      filter((users) => users.length > 0),
      take(1),
      map((users) =>
        users.map(
          (user) =>
            ({
              userId: user.id,
              firstName: user.firstName,
              lastName: user.lastName,
              availableVacationDays: null,
              availableSpecialVacationDays: null,
              carryover: null,
              takenVacationDays: null,
              takenSpecialVacationDays: null,
              plannedVacationDays: null,
              plannedSpecialVacationDays: null,
              balance: null,
              id: null,
              orgUnitId: user.organizationalUnitId,
            }) as unknown as UserVacationV2,
        ),
      ),
      finalize(() => {
        this.isLoadingSignal.set(false);
        this.showMask = false;
        this.visible = false;
        this.cdr.detectChanges();
        if (navigateToSpecificPage && this.gridService.gridApi) {
          this.gridService.gridApi.paginationGoToPage(
            this.currentUserPageSignal(),
          );
        }
      }),
    );
    this.cdr.detectChanges();
  }

  async saveVacations(vacations: UserVacationV2[]): Promise<void> {
    if (this.gridService.gridApi) {
      this.visible = false;
      this.gridService.gridApi.setGridOption('loading', true);
      this.currentUserPageSignal.set(
        this.gridService.gridApi.paginationGetCurrentPage(),
      );
      const year = getYear(this.selectedYear as Date);

      try {
        const hydratedVacations = await this.hydrateVacations(vacations, year);
        const mappedVacations = this.mapVacationObject(hydratedVacations, year);

        const { vacationsForCreate, vacationsForUpdate } =
          mappedVacations.reduce<{
            vacationsForCreate: UserVacation[];
            vacationsForUpdate: UserVacation[];
          }>(
            (result, vacation) => {
              if (vacation.id === null) {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { id, ...rest } = vacation;
                result.vacationsForCreate.push(rest);
              } else {
                result.vacationsForUpdate.push(vacation);
              }
              return result;
            },
            { vacationsForCreate: [], vacationsForUpdate: [] },
          );

        if (vacationsForCreate?.length) {
          await this.vacationManagementService.createUserVacations(
            vacationsForCreate,
          );
        }

        if (vacationsForUpdate?.length) {
          await this.processInBatches(
            vacationsForUpdate,
            3,
            async (vacationForUpdate) => {
              await this.vacationManagementService.updateUserVacationsV2(
                vacationForUpdate,
              );
            },
          );
        }
      } catch (error) {
        this.nzMessageService.error(
          this.translateService.instant('general.error'),
        );
        Sentry.captureException(error);
        this.cdr.detectChanges();
      } finally {
        this.gridService.gridApi.setGridOption('loading', false);
        this.loadUserVacationsByYear(year, true);
      }
    }
  }

  // TO-DO: Pls delete this trash when we have a proper bulk endpoint
  async processInBatches<T>(
    items: T[],
    batchSize: number,
    processFn: (item: T) => Promise<void>,
  ): Promise<void> {
    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize);
      const batchPromises = batch.map(processFn);
      await Promise.all(batchPromises);
    }
  }

  mapVacationObject(vacations: UserVacationV2[], year: number): UserVacation[] {
    const mappedVacations = vacations.map(
      ({
        availableVacationDays,
        userId,
        id,
        availableSpecialVacationDays,
        carryover,
      }) => ({
        year: year.toString(),
        numberOfDays: availableVacationDays as number,
        numberOfSpecialVacations: availableSpecialVacationDays,
        numberOfCarryOverVacations: carryover,
        userId,
        id,
      }),
    );

    return mappedVacations;
  }

  closeDrawer(): void {
    this.visible = false;
  }

  get selectedYearNumber(): number | null {
    return this.selectedYear ? getYear(this.selectedYear) : null;
  }

  onEdit(): void {
    this.visible = true;
  }

  private readonly exportColumnHeaderTranslationKeys: Record<string, string> = {
    name: 'general.name',
    orgUnitId: 'page.vacation.management.organizational_unit',
    leaveDays: 'page.vacation_management.leave_days',
    specialLeaveDays: 'page.vacation.management.special_leave_days',
    carriedOver: 'page.vacation.management.carried_over',
    usedLeaveDays: 'page.vacation.management.used_leave_days',
    usedSpecialLeaveDays: 'page.vacation.management.used_special_leave_days',
    plannedLeaveDays: 'page.vacation.management.planned_leave_days',
    plannedSpecialLeaveDays:
      'page.vacation.management.planned_special_leave_days',
    balance: 'page.vacation.management.balance',
  };

  async onExport(): Promise<void> {
    const rows: UserVacationV2[] = [];
    const date = new Date();
    const fileName = `[${this.localizationDatePipe.transform(date)}] ${this.translateService.instant(
      'page.vacation.management.export_vacation_overview',
    )} ${this.currentYear}.csv`;

    this.progressMessage().openProgressMessage();

    this.gridService.gridApi?.forEachNodeAfterFilter((node) => {
      if (node.data) rows.push(node.data);
    });

    try {
      this.isExporting.set(true);
      await this.cacheVacationsForExport(rows, this.currentYear);

      this.gridService.gridApi?.exportDataAsCsv({
        fileName,
        prependContent: 'sep=,',
        columnKeys: [
          'name',
          'orgUnitId',
          'leaveDays',
          'specialLeaveDays',
          'carriedOver',
          'usedLeaveDays',
          'usedSpecialLeaveDays',
          'plannedLeaveDays',
          'plannedSpecialLeaveDays',
          'balance',
        ],
        processHeaderCallback: (params) => {
          const translationKey =
            this.exportColumnHeaderTranslationKeys[params.column.getColId()];
          return translationKey
            ? this.translateService.instant(translationKey)
            : (params.column.getColDef().headerName ?? '');
        },
        processCellCallback: (params) => {
          if (params.column.getColId() === 'orgUnitId') {
            return this.orgUnitsMap.get(params.value)?.name ?? '';
          }
          if (typeof params.value !== 'number') {
            return String(params.value ?? '');
          }
          return String(
            this.localizedNumberPipe.transform(params.value, 'decimal') ??
              params.value,
          );
        },
      });
    } catch (error) {
      this.nzMessageService.error(
        this.translateService.instant('general.error'),
      );
      Sentry.captureException(error);
    } finally {
      this.progressMessage().closeProgressMessage();
      this.isExporting.set(false);
    }
  }

  onYearSelection(selectedDate: Date): void {
    this.selectedYear = selectedDate;
    const year = getYear(this.selectedYear);
    this.loadUserVacationsByYear(year);
  }

  public numberOfLeaveDaysValueGetter(
    params: ValueGetterParams,
  ): string | number {
    const cached = this.batchService.getCachedVacation(
      params.data.userId,
      this.currentYear,
    );
    if (cached) {
      return cached.availableVacationDays ?? '—';
    }
    return params.data.availableVacationDays ?? '—';
  }

  public numberOfSpecialLeaveDaysValueGetter(
    params: ValueGetterParams,
  ): string | number {
    const cached = this.batchService.getCachedVacation(
      params.data.userId,
      this.currentYear,
    );
    if (cached) {
      return cached.availableSpecialVacationDays ?? '—';
    }
    return params.data.availableSpecialVacationDays ?? '—';
  }

  public numberOfCarriedOverDaysValueGetter(
    params: ValueGetterParams,
  ): string | number {
    const cached = this.batchService.getCachedVacation(
      params.data.userId,
      this.currentYear,
    );
    if (cached) {
      return cached.carryover ?? '—';
    }
    return params.data.carryover ?? '—';
  }

  public numberOfUsedLeaveDaysValueGetter(
    params: ValueGetterParams,
  ): string | number {
    const cached = this.batchService.getCachedVacation(
      params.data.userId,
      this.currentYear,
    );
    if (cached) {
      return cached.takenVacationDays ?? '—';
    }
    return params.data.takenVacationDays ?? '—';
  }

  public numberOfUsedSpecialLeaveDaysValueGetter(
    params: ValueGetterParams,
  ): string | number {
    const cached = this.batchService.getCachedVacation(
      params.data.userId,
      this.currentYear,
    );
    if (cached) {
      return cached.takenSpecialVacationDays ?? '—';
    }
    return params.data.takenSpecialVacationDays ?? '—';
  }

  public numberOfPlannedLeaveDaysValueGetter(
    params: ValueGetterParams,
  ): string | number {
    const cached = this.batchService.getCachedVacation(
      params.data.userId,
      this.currentYear,
    );
    if (cached) {
      return cached.plannedVacationDays ?? '—';
    }
    return params.data.plannedVacationDays ?? '—';
  }

  public numberOfPlannedSpecialLeaveDaysValueGetter(
    params: ValueGetterParams,
  ): string | number {
    const cached = this.batchService.getCachedVacation(
      params.data.userId,
      this.currentYear,
    );
    if (cached) {
      return cached.plannedSpecialVacationDays ?? '—';
    }
    return params.data.plannedSpecialVacationDays ?? '—';
  }

  public balanceDaysValueGetter(params: ValueGetterParams): string | number {
    const cached = this.batchService.getCachedVacation(
      params.data.userId,
      this.currentYear,
    );
    if (cached) {
      const fixedNumber =
        typeof cached.balance === 'number'
          ? parseFloat(cached.balance.toFixed(2))
          : null;
      return fixedNumber ?? '—';
    }

    const data = params.data as UserVacationV2;
    const fixedNumber =
      typeof data.balance === 'number'
        ? parseFloat(data.balance.toFixed(2))
        : null;

    return fixedNumber ?? '—';
  }
}
